import React, { useState, useEffect } from 'react';
import { Search, Download, Key, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';

// Use relative URL - will be proxied to production backend via setupProxy.js
const API_URL = process.env.REACT_APP_API_URL || '';

const SoftwareLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch manufacturers - API returns {total, limit, offset, manufacturers: [...]}
      const mfrResponse = await fetch(`${API_URL}/api/trm/manufacturers`);
      if (!mfrResponse.ok) throw new Error('Failed to fetch manufacturers');
      const mfrData = await mfrResponse.json();
      const manufacturersList = mfrData.manufacturers || [];
      setManufacturers(manufacturersList);

      // Mock license data - in real app, this would come from an API endpoint
      // You can create a real endpoint later for licenses
      const mockLicenses = generateMockLicenses(manufacturersList);
      setLicenses(mockLicenses);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate mock license data based on manufacturers
  const generateMockLicenses = (mfrs) => {
    const licenseTypes = ['Enterprise', 'Professional', 'Standard', 'Developer'];
    const statuses = ['Active', 'Expiring Soon', 'Expired', 'Pending Renewal'];
    const mockData = [];

    mfrs.slice(0, 25).forEach((mfr, idx) => {
      mockData.push({
        id: `LIC-${String(idx + 1).padStart(4, '0')}`,
        manufacturer: mfr.canonical_name,
        manufacturer_id: mfr.manufacturer_id,
        product_name: `${mfr.canonical_name} Suite`,
        license_type: licenseTypes[idx % licenseTypes.length],
        seats: Math.floor(Math.random() * 500) + 50,
        used_seats: Math.floor(Math.random() * 400) + 20,
        expiration_date: new Date(Date.now() + (Math.random() * 365 - 90) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        annual_cost: Math.floor(Math.random() * 500000) + 50000,
        status: statuses[idx % statuses.length],
        contract_number: `CON-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`
      });
    });

    return mockData;
  };

  const getStatusBadge = (status) => {
    const configs = {
      'Active': { bg: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))', color: 'var(--cares-green)', icon: CheckCircle },
      'Expiring Soon': { bg: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))', color: 'var(--cares-amber)', icon: Clock },
      'Expired': { bg: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))', color: 'var(--cares-orange)', icon: XCircle },
      'Pending Renewal': { bg: 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))', color: 'var(--cares-blue)', icon: AlertCircle }
    };
    const config = configs[status] || configs['Active'];
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '12px',
        background: config.bg,
        color: config.color,
        fontSize: '13px',
        fontWeight: '500'
      }}>
        <Icon style={{ width: '14px', height: '14px' }} />
        {status}
      </span>
    );
  };

  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = 
      license.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.contract_number.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesManufacturer = 
      selectedManufacturer === 'all' || 
      license.manufacturer_id === parseInt(selectedManufacturer);

    return matchesSearch && matchesManufacturer;
  });

  const totalAnnualCost = filteredLicenses.reduce((sum, lic) => sum + lic.annual_cost, 0);
  const totalSeats = filteredLicenses.reduce((sum, lic) => sum + lic.seats, 0);
  const totalUsedSeats = filteredLicenses.reduce((sum, lic) => sum + lic.used_seats, 0);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p style={{ marginTop: '16px', color: 'var(--cares-text-label)' }}>Loading license data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertCircle style={{ color: 'var(--cares-orange)', width: '24px', height: '24px' }} />
          <div>
            <h3 style={{ color: 'var(--cares-orange)', fontWeight: '600', marginBottom: '4px' }}>Error Loading Data</h3>
            <p style={{ color: 'var(--cares-orange)' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: 'var(--cares-text-1)', marginBottom: '8px' }}>
          Software Licenses
        </h1>
        <p style={{ color: 'var(--cares-text-label)', fontSize: '16px' }}>
          Track and manage software licenses, seats, and renewal dates
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ opacity: 0.9, marginBottom: '8px' }}>Total Licenses</p>
          <p style={{ fontSize: '32px', fontWeight: '700' }}>{filteredLicenses.length}</p>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-magenta) 0%, var(--cares-magenta) 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ opacity: 0.9, marginBottom: '8px' }}>Annual Cost</p>
          <p style={{ fontSize: '32px', fontWeight: '700' }}>${(totalAnnualCost / 1000000).toFixed(2)}M</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-teal) 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ opacity: 0.9, marginBottom: '8px' }}>Total Seats</p>
          <p style={{ fontSize: '32px', fontWeight: '700' }}>{totalSeats.toLocaleString()}</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-teal) 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ opacity: 0.9, marginBottom: '8px' }}>Utilization</p>
          <p style={{ fontSize: '32px', fontWeight: '700' }}>{((totalUsedSeats / totalSeats) * 100).toFixed(0)}%</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div style={{
        background: 'var(--cares-surface-card)',
        border: '1px solid var(--cares-line-card)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Search */}
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: 'var(--cares-text-note)'
          }} />
          <input
            type="text"
            placeholder="Search licenses, manufacturers, contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Manufacturer Filter */}
        <select
          value={selectedManufacturer}
          onChange={(e) => setSelectedManufacturer(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--cares-line-strong)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            cursor: 'pointer',
            minWidth: '200px'
          }}
        >
          <option value="all">All Manufacturers</option>
          {manufacturers.map(mfr => (
            <option key={mfr.manufacturer_id} value={mfr.manufacturer_id}>
              {mfr.canonical_name}
            </option>
          ))}
        </select>

        {/* Export Button */}
        <button style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          background: 'var(--cares-blue)',
          color: 'var(--cares-text-max)',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer'
        }}>
          <Download style={{ width: '16px', height: '16px' }} />
          Export
        </button>
      </div>

      {/* Licenses Table */}
      <div style={{
        background: 'var(--cares-surface-card)',
        border: '1px solid var(--cares-line-card)',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '1px solid var(--cares-line-card)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  License ID
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Manufacturer
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Product
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Type
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Seats
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Annual Cost
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Expiration
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLicenses.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: 'var(--cares-text-note)' }}>
                    No licenses found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredLicenses.map((license) => (
                  <tr
                    key={license.id}
                    style={{ borderBottom: '1px solid var(--cares-line)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                  >
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Key style={{ width: '16px', height: '16px', color: 'var(--cares-text-label)' }} />
                        {license.id}
                      </div>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-3)' }}>
                      {license.manufacturer}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-3)' }}>
                      {license.product_name}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-3)' }}>
                      {license.license_type}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-3)', textAlign: 'right' }}>
                      <div>
                        <div style={{ fontWeight: '500', color: 'var(--cares-text-1)' }}>
                          {license.used_seats.toLocaleString()} / {license.seats.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>
                          {((license.used_seats / license.seats) * 100).toFixed(0)}% used
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '600', textAlign: 'right' }}>
                      ${license.annual_cost.toLocaleString()}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-3)' }}>
                      {new Date(license.expiration_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td style={{ padding: '16px' }}>
                      {getStatusBadge(license.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: 'var(--cares-surface-board)',
        borderRadius: '8px',
        fontSize: '14px',
        color: 'var(--cares-text-label)'
      }}>
        Showing {filteredLicenses.length} of {licenses.length} licenses
      </div>
    </div>
  );
};

export default SoftwareLicenses;
